/**
 * Segments Page — list segments with counts, create/edit, view members.
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pin, PinOff, Trash2, Users, Filter } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { SegmentBuilder } from '@/components/crm/SegmentBuilder'
import type { Segment } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  EmptyState,
  useToast,
} from '@/components/ui'

export function SegmentsPage() {
  const { fetchSegments, deleteSegment, toggleSegmentPin } = useCRM()
  const segments = useCrmStore((s) => s.segments)
  const setActiveSegment = useCrmStore((s) => s.setActiveSegment)
  const isLoading = useCrmStore((s) => s.isLoadingSegments)
  const navigate = useNavigate()
  const { addToast } = useToast()

  const [showBuilder, setShowBuilder] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    fetchSegments()
  }, [fetchSegments])

  const handleViewSegment = useCallback((segment: Segment) => {
    setActiveSegment(segment)
    // Navigate to contacts view filtered by this segment
    navigate(`/crm/contacts?segment=${segment.id}`)
  }, [setActiveSegment, navigate])

  const handleConfirmedDelete = useCallback(async () => {
    if (!confirmDelete) return
    setIsDeleting(true)
    const ok = await deleteSegment(confirmDelete)
    setIsDeleting(false)
    if (ok) {
      addToast({ type: 'success', title: 'Segment deleted' })
    } else {
      addToast({ type: 'error', title: 'Delete failed' })
    }
    setConfirmDelete(null)
  }, [confirmDelete, deleteSegment, addToast])

  const pinnedSegments = segments.filter((s) => s.isPinned)
  const unpinnedSegments = segments.filter((s) => !s.isPinned)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border">
        <div className="py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{segments.length} segments</p>
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowBuilder(true)}>
            New Segment
          </Button>
        </div>
      </div>

      {/* Segment list */}
      <div className="flex-1 overflow-auto py-6">
        {isLoading && segments.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <Spinner />
          </div>
        ) : segments.length === 0 ? (
          <EmptyState icon={Filter} title="No segments yet" />
        ) : (
          <div className="space-y-6">
            {/* Pinned segments */}
            {pinnedSegments.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Pinned</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pinnedSegments.map((segment) => (
                    <SegmentCard
                      key={segment.id}
                      segment={segment}
                      onView={handleViewSegment}
                      onDelete={setConfirmDelete}
                      onTogglePin={toggleSegmentPin}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Other segments */}
            {unpinnedSegments.length > 0 && (
              <div>
                {pinnedSegments.length > 0 && (
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Other</h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {unpinnedSegments.map((segment) => (
                    <SegmentCard
                      key={segment.id}
                      segment={segment}
                      onView={handleViewSegment}
                      onDelete={setConfirmDelete}
                      onTogglePin={toggleSegmentPin}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Builder modal */}
      {showBuilder && (
        <SegmentBuilder onClose={() => { setShowBuilder(false); fetchSegments() }} />
      )}

      {confirmDelete && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Delete this segment?</ModalTitle></ModalHeader>
            <ModalBody><p className="text-sm text-muted-foreground">This cannot be undone.</p></ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="destructive" isLoading={isDeleting} onClick={handleConfirmedDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  )
}

// ── Segment Card ────────────────────────────────────────────────────────────

function SegmentCard({ segment, onView, onDelete, onTogglePin }: {
  segment: Segment
  onView: (s: Segment) => void
  onDelete: (id: string) => void
  onTogglePin: (id: string) => void
}) {
  return (
    <div
      onClick={() => onView(segment)}
      className="bg-card border border-border rounded-lg p-4 hover:border-primary/40 hover:shadow-xs cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {segment.color && (
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: segment.color }} />
          )}
          <h3 className="text-sm font-medium text-foreground">{segment.name}</h3>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            aria-label={segment.isPinned ? 'Unpin' : 'Pin'}
            title={segment.isPinned ? 'Unpin' : 'Pin'}
            onClick={(e) => { e.stopPropagation(); onTogglePin(segment.id) }}
          >
            {segment.isPinned ? <PinOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Pin className="w-3.5 h-3.5 text-muted-foreground" />}
          </Button>
          {!segment.isSystem && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Delete segment"
              className="hover:bg-destructive/10"
              onClick={(e) => { e.stopPropagation(); onDelete(segment.id) }}
            >
              <Trash2 className="w-3.5 h-3.5 text-destructive" />
            </Button>
          )}
        </div>
      </div>

      {segment.description && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{segment.description}</p>
      )}

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Users className="w-3.5 h-3.5" />
        <span className="font-medium">{segment.cachedCount}</span>
        <span>contacts</span>
        {segment.isSystem && (
          <span className="ml-auto px-1.5 py-0.5 bg-muted rounded-sm text-[10px] text-muted-foreground">System</span>
        )}
      </div>
    </div>
  )
}
